import React from 'react'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'

import { Button } from '@wartek-id/button-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'
import { Spinner } from '@wartek-id/spinner'

import { Header } from 'app/KinerjaShared/components/Header'
import { PerilakuKerjaItem } from 'app/KinerjaPSPegawaiPage/pages/Perencanaan/PerilakuKerja/PerilakuKerjaItem'
import DialogConfirmation from 'app/KinerjaPSPegawaiPage/components/DialogConfirmation'
import DialogInfoPerilakuKerja from 'app/KinerjaPSPegawaiPage/pages/Perencanaan/PerilakuKerja/DialogInfoPerilakuKerja'
import ScrollDown from 'app/KinerjaPSPegawaiPage/components/ScrollDown'
import { usePerilakuKerjaStore } from 'app/KinerjaPSPegawaiPage/stores/kinerja-ps'
import { CONTENT_PERILAKU_KERJA } from 'app/KinerjaPSPegawaiPage/configs/content-perilaku-kerja'
import { PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH } from 'app/KinerjaShared/constants/path'
import { useAPISubmitPerilakuKerja } from 'api/kinerja/pengawas-sekolah/perencanaan'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { usePerencanaanPS } from 'app/KinerjaPSPegawaiPage/hooks/usePerencanaan'
import { useStoreByUserId } from 'app/KinerjaPSPegawaiPage/hooks/useStoreByUserId'
import useWarn from 'app/KinerjaShared/hooks/useWarn'
import { usePPSTrackEvent } from 'app/KinerjaPSPegawaiPage/hooks/usePPSTrackEvent'

import type { PSPerilakuKerjaAspek } from 'types/kinerja-ps-pegawai'

const parsePerilakuKerjaValues = (source: PSPerilakuKerjaAspek[]) => {
  return CONTENT_PERILAKU_KERJA.aspek.map(
    ({ value, indikator: indikatorValues }) => {
      const indikator = source.find((p) => p.aspek === value)?.indikator
      const ekspektasi = source.find((p) => p.aspek === value)?.ekspektasi

      // check if the indikator and ekspektasi is still valid
      const existingIndikator = indikatorValues.find(
        (ind) => ind.value === indikator
      )
      const existingEkspektasi = existingIndikator?.ekspektasi.find(
        (eks) => eks.value === ekspektasi
      )

      return {
        aspek: value,
        indikator: existingIndikator?.value,
        ekspektasi: existingEkspektasi?.value,
      }
    }
  )
}

function PSPerencanaanPerilakuKerjaPage(_props: any) {
  const router = useRouter()
  const toast = useToast()
  const { trackEvent } = usePPSTrackEvent()

  const {
    selectedPerilakuKerjaDraft,

    saveDraft,
  } = usePerilakuKerjaStore((state) => ({
    selectedPerilakuKerjaDraft: state.selectedPerilakuKerja,

    saveDraft: state.saveDraft,
  }))
  const { period } = usePeriodContext()

  const { perencanaan, apiStatus, isPerilakuKerjaEditable } = usePerencanaanPS()

  // set Store by User ID
  useStoreByUserId()

  const [selectedPerilakuKerja, setSelectedPerilakuKerja] = useState<
    PSPerilakuKerjaAspek[]
  >(
    CONTENT_PERILAKU_KERJA.aspek.map((a) => ({
      aspek: a.value,
    }))
  )

  const [isFormDirty, setIsFormDirty] = useState(false)
  const [isFormValid, setIsFormValid] = useState(true)
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isSaveBeforeCloseDialogOpen, setSaveBeforeCloseDialogOpen] =
    useState(false)
  const [isInfoDialogOpen, setIsInfoDialogOpen] = useState(false)
  const isPlanningFinalized = !isPerilakuKerjaEditable

  useEffect(() => {
    if (apiStatus === 'success') {
      try {
        const parsedPerilakuKerjaValues = JSON.parse(
          perencanaan.perilakuKerjaValues
        )
        const perilakuKerjaValues = parsePerilakuKerjaValues(
          selectedPerilakuKerjaDraft.length
            ? selectedPerilakuKerjaDraft
            : parsedPerilakuKerjaValues
        )
        setSelectedPerilakuKerja(perilakuKerjaValues)
      } catch (e) {
        console.error(e)

        // if there's an error, fallback to the draft
        const perilakuKerjaValues = parsePerilakuKerjaValues(
          selectedPerilakuKerjaDraft
        )
        setSelectedPerilakuKerja(perilakuKerjaValues)
      }
    }
  }, [apiStatus, perencanaan, selectedPerilakuKerjaDraft])

  const setIndikator = (index, value) => {
    setIsFormDirty(true)
    const aspek = CONTENT_PERILAKU_KERJA.aspek[index]
    const indikator = aspek.indikator.find((i) => i.value === value)

    const copyOfSelectedPerilakuKerja = [...selectedPerilakuKerja]

    copyOfSelectedPerilakuKerja[index].indikator = indikator.value
    copyOfSelectedPerilakuKerja[index].ekspektasi = undefined

    setSelectedPerilakuKerja(copyOfSelectedPerilakuKerja)
  }

  const setEkspektasi = (index, indikatorValue, value) => {
    setIsFormDirty(true)

    const aspek = CONTENT_PERILAKU_KERJA.aspek[index]
    const indikator = aspek.indikator.find((i) => i.value === indikatorValue)
    const ekspektasi = indikator.ekspektasi.find((e) => e.value === value)

    const copyOfSelectedPerilakuKerja = [...selectedPerilakuKerja]

    copyOfSelectedPerilakuKerja[index].ekspektasi = ekspektasi.value

    setSelectedPerilakuKerja(copyOfSelectedPerilakuKerja)
  }

  const validate = useCallback(() => {
    const checkValid = selectedPerilakuKerja.every((e) => !!e.ekspektasi)

    setIsFormValid(checkValid)

    return checkValid
  }, [selectedPerilakuKerja])

  const { mutateAsync: submit, status } = useAPISubmitPerilakuKerja()

  const canSubmit =
    selectedPerilakuKerja.length > 0 &&
    selectedPerilakuKerja.every((e) => !!e.ekspektasi)

  const { reset: resetPreventRouteChange } = useWarn({
    enabled: isFormDirty,
    bindPopState: true,
    onWarn: () => {
      setSaveBeforeCloseDialogOpen(true)
    },
  })

  const onNext = () => {
    if (validate()) setIsSaveDialogOpen(true)
  }

  const onSubmit = useCallback(async () => {
    try {
      await submit({
        periodeId: period.id,
        perilakuKerjaValues: JSON.stringify(selectedPerilakuKerja),
      })

      // Track Submission
      trackEvent('perencanaan pegawai', 'save draft perencanaan', {
        aspect: 'perilaku kerja',
      })

      // Reset state
      saveDraft([])
      resetPreventRouteChange()

      // Display toast
      toast({
        message: 'Berhasil simpan Perilaku Kerja!',
        type: 'success',
      })

      // Redirect to Beranda
      router.push(`${PENGELOLAAN_KINERJA_PS_PEGAWAI_PATH}/perencanaan/beranda`)
    } catch (e) {
      toast({
        message: 'Gagal simpan Perilaku Kerja. Coba lagi.',
      })
    } finally {
      setIsSaveDialogOpen(false)
    }
  }, [
    submit,
    period.id,
    selectedPerilakuKerja,
    trackEvent,
    saveDraft,
    resetPreventRouteChange,
    toast,
    router,
  ])

  const saveCurrentDraft = useCallback(() => {
    saveDraft(selectedPerilakuKerja)

    // Track save draft
    trackEvent('perencanaan pegawai', 'save draft perencanaan', {
      aspect: 'perilaku kerja',
    })

    // Reset exit prevention
    resetPreventRouteChange()

    // Display toast
    toast({
      message: 'Berhasil simpan Perilaku Kerja!',
      type: 'success',
    })

    router.back()
  }, [
    saveDraft,
    selectedPerilakuKerja,
    trackEvent,
    resetPreventRouteChange,
    toast,
    router,
  ])

  if (apiStatus === 'loading') {
    return (
      <div className="flex justify-center w-full">
        <Spinner data-testid="loading-spinner-perilaku-kerja" />
      </div>
    )
  }

  return (
    <div className={clsx(isPlanningFinalized ? 'pb-16' : 'pb-80')}>
      <Header
        title="Perilaku Kerja"
        className="mb-9 print:hidden"
        onBack={() => {
          if (isFormDirty) {
            setSaveBeforeCloseDialogOpen(true)
          } else {
            router.back()
          }
        }}
        rightNode={
          <section className="py-1 bg-white rounded">
            <Button
              as="a"
              variant="ghost"
              className="flex gap-1.5"
              size="md"
              onClick={() => setIsInfoDialogOpen(true)}
            >
              <Icon fontSize="sm" color="informational">
                help
              </Icon>
              Tentang Perilaku Kerja
            </Button>
          </section>
        }
      />

      <DialogInfoPerilakuKerja
        isOpen={isInfoDialogOpen}
        onClose={() => setIsInfoDialogOpen(false)}
      />

      <section className="flex flex-col gap-3 mt-12 bg-white rounded-lg">
        <header className="p-3 rounded-t-lg h-[72px] flex items-center justify-center bg-expressive-dark-blue">
          <Text variant="heading-md" color="inverse">
            Pilih 1 Indikator dan Perwujudan
          </Text>
        </header>

        <section className="px-4">
          {CONTENT_PERILAKU_KERJA.aspek.map((a, index) => (
            <PerilakuKerjaItem
              key={index}
              aspek={a}
              index={index}
              selectedIndikatorValue={selectedPerilakuKerja[index]?.indikator}
              selectedEkspektasiValue={selectedPerilakuKerja[index]?.ekspektasi}
              isReadOnly={isPlanningFinalized}
              onChangeIndikator={(value) => {
                setIndikator(index, value.name)
              }}
              onChangeEkspektasi={(indikatorValue, value) => {
                setEkspektasi(index, indikatorValue, value.name)
              }}
              isIndikatorInvalid={
                !isFormValid && !selectedPerilakuKerja[index]?.indikator
              }
              isEkpektasiInvalid={
                !isFormValid && !selectedPerilakuKerja[index]?.ekspektasi
              }
            />
          ))}
        </section>
      </section>

      {!isPlanningFinalized && (
        <div className="fixed bottom-0 left-0 right-0 z-10 flex justify-center w-full p-4 bg-white border-t border-subdued">
          <Button
            color={!canSubmit ? 'black' : 'blue'}
            variant={!canSubmit ? 'ghost' : 'solid'}
            size="md"
            className={clsx(
              '!px-6',
              !canSubmit && '!bg-gray-30 !text-gray-50 !cursor-not-allowed'
            )}
            onClick={onNext}
          >
            Simpan
            <Icon color={!canSubmit ? 'disabled' : 'inverse'}>
              chevron_right
            </Icon>
          </Button>
        </div>
      )}

      <DialogConfirmation
        title="Simpan Perilaku Kerja?"
        description="Setelah disimpan, Anda dapat ubah lagi sesuai kebutuhan atau langsung ajukan."
        isOpen={isSaveDialogOpen}
        onClose={() => setIsSaveDialogOpen(false)}
        onClickCancel={() => setIsSaveDialogOpen(false)}
        onClickConfirm={onSubmit}
        ctaConfirm="Simpan"
        isLoading={status === 'loading'}
      />

      <DialogConfirmation
        title="Simpan sebelum keluar?"
        description="Pilihan Anda akan disimpan dan masih dapat diubah selama Perencanaan belum diajukan."
        isOpen={isSaveBeforeCloseDialogOpen}
        onClose={() => {
          setSaveBeforeCloseDialogOpen(false)
        }}
        onClickConfirm={saveCurrentDraft}
        onClickCancel={() => setSaveBeforeCloseDialogOpen(false)}
        ctaConfirm="Simpan dan keluar"
      />

      <ScrollDown />
    </div>
  )
}

export default PSPerencanaanPerilakuKerjaPage
